import { useMutation, useQuery } from "@apollo/client";
import {
    DeleteUserFiltersPreferences,
    GetUserFiltersPreferences,
    GetUserNotificationSettings,
    IEnabledMillNotificationSettingsQuery,
    IGetUserFiltersPreferencesQuery,
    IGetUserNotificationSettingsQuery,
    UpsertUserFiltersPreferences,
    enabledMillNotificationSettings,
    getAllUsers,
    getApiAdminProfile,
    getCurrentUserData,
    getPaginatedUsers,
    getUserTypes,
    getUsersByType,
    softDeleteUser,
    updateUserMachineCenterNotificationSettings,
    updateUserNotificationSettings,
    upsertUser,
} from "api/queries/usersAPI";
import { useEffect, useState } from "react";
import { IUser, IUserType, IUsersEntity } from "./UsersEntityInterfaces";
import { FiltersPreferencesReportsEnum, ModuleNotificationsEnum } from "constants/constants";

export const UsersEntity: IUsersEntity = {
    GetPaginatedUsers: (mill_id: number, limit: number, offset: number) => {
        const { loading, error, data, refetch } = useQuery(getPaginatedUsers, {
            variables: { mill_id, offset, limit },
        });
        let users: IUser[] = [] as IUser[];
        let totalCount = 0;
        if (data) {
            users = data.user;
            totalCount = data.user_aggregate.aggregate.totalCount;
        }
        return { loading, users, totalCount, error, refetch };
    },
    GetUserTypes: () => {
        const { loading, error, data } = useQuery(getUserTypes);
        let userTypes: IUserType[] = [] as IUserType[];
        if (data) {
            userTypes = data.user_type;
        }
        return { loading, userTypes, error };
    },
    GetCurrentUser: (user_id: number, historic?: boolean, skip?: boolean) => {
        const [enabledNotifications, setEnabledNotifiactions] = useState<boolean>(false);
        const [enabledNotificationsIds, setEnabledNotifiactionsIds] = useState<number[]>([]);
        const [currentUser, setCurrentUser] = useState<IUser>({} as IUser);

        // `skip` exists for the API Administrator (OS-2557): this document selects the `mill`
        // relationship and a `mill` root field, which the api_administrator role is not granted.
        // The portal fetches its own profile via GetApiAdminProfile instead.
        const { loading, error, refetch } = useQuery(getCurrentUserData, {
            skip,
            variables: { where: { user_id: { _eq: user_id }, ...(!historic && { is_enabled: { _eq: true } }) } },
            onCompleted: (data) => {
                const currentUser = data.user[0];
                setCurrentUser(currentUser);
            },
        });

        useQuery(enabledMillNotificationSettings, {
            skip: skip || !currentUser?.user_type?.user_type_id,
            variables: { userTypeId: currentUser?.user_type?.user_type_id },
            fetchPolicy: "no-cache",
            onCompleted: (data: IEnabledMillNotificationSettingsQuery) => {
                const hasEnabledNotifications = !!data.email_notification_settings.length;
                const notificationsIds = data.email_notification_settings.map(
                    (notification) => notification.notification_type_id,
                );
                setEnabledNotifiactions(hasEnabledNotifications);
                setEnabledNotifiactionsIds(notificationsIds);
            },
        });

        return { loading, currentUser, enabledNotifications, enabledNotificationsIds, error, refetch };
    },
    GetApiAdminProfile: (user_id: number) => {
        // Deliberately undefined-until-resolved rather than `{} as IUser`: an empty object is
        // truthy, so consumers cannot tell "profile not loaded" from "profile loaded". The query
        // legitimately returns zero rows (user_id missing from localStorage, or the account
        // disabled mid-session), and onCompleted does not fire at all on error.
        const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);

        const { loading, error, refetch } = useQuery(getApiAdminProfile, {
            variables: { where: { user_id: { _eq: user_id }, is_enabled: { _eq: true } } },
            onCompleted: (data) => {
                setCurrentUser(data.user?.[0]);
            },
            onError: () => {
                setCurrentUser(undefined);
            },
        });

        return { loading, currentUser, error, refetch };
    },
    UpsertUser: () => {
        const [upsertUserData, { error, loading }] = useMutation(upsertUser);
        return { error, upsertUserData, loading };
    },
    GetUsersByType: (user_type_ids: number[], millId?: number) => {
        const { loading, error, data } = useQuery(getUsersByType, {
            variables: {
                where: {
                    _and: [
                        { user_type_id: { _in: user_type_ids } },
                        { is_enabled: { _eq: true } },
                        ...(millId ? [{ _or: [{ mill_id: { _eq: millId } }, { mill_id: { _is_null: true } }] }] : []),
                        {
                            _or: [
                                { avatar_media_id: { _is_null: true } },
                                {
                                    _and: [
                                        { media: { media_id: { _is_null: false } } },
                                        { media: { is_enabled: { _eq: true } } },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            },
        });
        let users: IUser[] = [] as IUser[];
        if (data) {
            users = data.user;
        }
        return { loading, users, error };
    },
    SoftDeleteUser: () => {
        const [deleteUser, { error }] = useMutation(softDeleteUser);

        return { apolloMutationError: error, deleteUser };
    },
    GetAllUsers: (filterByUserType?: number[], millId?: number | string) => {
        const { data, error, loading, refetch } = useQuery(getAllUsers, {
            fetchPolicy: "no-cache",
            variables: {
                where: {
                    is_enabled: { _eq: true },
                    ...(filterByUserType ? { user_type_id: { _in: filterByUserType } } : {}),
                    ...(millId ? { _or: [{ mill_id: { _eq: millId } }, { mill_id: { _is_null: true } }] } : {}),
                },
            },
        });
        const [users, setUsers] = useState<IUser[]>([]);

        useEffect(() => {
            if (data) {
                setUsers(data.user);
            }
        }, [data]);

        return { apolloQueryError: error, users, loading, refetch };
    },
    GetUserNotificationSettings: (corporateEnabledNotifications: number[]) => {
        const [birchNotificationSettings, setBirchNotificationSettings] = useState<
            IGetUserNotificationSettingsQuery["user_notification_settings"]
        >([]);
        const [aspenNotificationSettings, setAspenNotificationSettings] = useState<
            IGetUserNotificationSettingsQuery["user_notification_settings"]
        >([]);

        const { error, loading, refetch } = useQuery(GetUserNotificationSettings, {
            fetchPolicy: "no-cache",
            variables: { corporateEnabledNotifications: corporateEnabledNotifications },
            onCompleted: (data: IGetUserNotificationSettingsQuery) => {
                const aspenNotifications = data.user_notification_settings.filter(
                    (setting) => setting.email_notification_type.module_type_id === ModuleNotificationsEnum.ASPEN,
                );
                const birchNotifications = data.user_notification_settings.filter(
                    (setting) => setting.email_notification_type.module_type_id === ModuleNotificationsEnum.BIRCH,
                );
                setAspenNotificationSettings(aspenNotifications);
                setBirchNotificationSettings(birchNotifications);
            },
        });

        return { error, aspenNotificationSettings, birchNotificationSettings, loading, refetch };
    },
    EnableUserNotificationSetting: () => {
        const [enableNotification, { error }] = useMutation(updateUserNotificationSettings);

        return { apolloMutationError: error, enableNotification };
    },

    EnableUserMachineCenterNotificationSetting: () => {
        const [enableMachineCenterNotification, { error }] = useMutation(updateUserMachineCenterNotificationSettings);

        return { apolloMutationError: error, enableMachineCenterNotification };
    },
    UpsertUserFiltersDefaultPreference: () => {
        const [upsertOperation, { error, loading }] = useMutation(UpsertUserFiltersPreferences);

        return { apolloMutationError: error, upsertOperation, loading };
    },
    RemoveUserFiltersDefaultPreference: () => {
        const [removeOperation, { error, loading }] = useMutation(DeleteUserFiltersPreferences);

        return { apolloMutationError: error, removeOperation, loading };
    },
    GetUserFiltersPreferences: (
        onCompleted: (data: IGetUserFiltersPreferencesQuery) => void,
        reportId: FiltersPreferencesReportsEnum,
        userId?: number,
    ) => {
        const { error, loading, refetch } = useQuery<IGetUserFiltersPreferencesQuery>(GetUserFiltersPreferences, {
            fetchPolicy: "no-cache",
            skip: !userId,
            variables: {
                where: {
                    user_id: { _eq: userId },
                    report_id: { _eq: reportId },
                },
            },
            onCompleted: onCompleted,
        });

        return { error, loading, refetch };
    },
};
